import { GridOptions, RowNode } from 'ag-grid-community';
import { DataGridColumn, DataGridServer, KatDataGrid } from './data-grid';

export interface RowEditOptions {
  isRowEditableCondition?: (row: any) => boolean;
  saveOnExit: boolean;
}

interface SubBuilder {
  actionColumnRenderer?: (
    actionColumnRenderer: KatDataGrid['actionColumnRenderer']
  ) => SubBuilder;
  enableRowEditing?: (rowEditOptions?: RowEditOptions) => SubBuilder;
  disabledRows?: (isRowDisabledCondition: (row: any) => boolean) => SubBuilder;
  agGridOptionValues?: (options: GridOptions) => { apply: () => void };
  apply: () => void;
}

export interface BuilderConfig {
  primaryKeyField: string | undefined;
  columns: DataGridColumn[] | undefined;
  isRowEditableCondition?: (row: any) => boolean;
  server: DataGridServer | undefined;
  enableRowEdit: boolean;
  saveOnExit: boolean;
  actionColumnRenderer: KatDataGrid['actionColumnRenderer'];
  isRowDisabledCondition?: (row: RowNode) => boolean;
  agGridOptionValues?: GridOptions;
}

export class DataGridConfigurationBuilder {
  private dataGrid: KatDataGrid;
  private columns: DataGridColumn[] = [];
  private server?: DataGridServer | undefined;
  private agGridOptionValues?: GridOptions;
  private isRowEditableCondition?: (row: RowNode) => boolean;
  private actionColumnRenderer: KatDataGrid['actionColumnRenderer'] = null;
  private saveOnExit = false;
  private primaryKeyField?: string;
  private enableRowEdit = false;
  private isRowDisabledCondition?: (row: RowNode) => boolean;

  constructor(dataGrid: KatDataGrid) {
    this.dataGrid = dataGrid;
  }

  configure(
    required: {
      primaryKeyField?: string;
      columns: DataGridColumn[] | null;
      server?: DataGridServer;
    } = {
      columns: null,
    }
  ) {
    this.primaryKeyField = required.primaryKeyField;
    this.columns = required.columns;
    if (required.server) {
      this.server = required.server;
    }
    if (!required.columns) {
      throw new Error('Columns must be defined');
    }

    // The final Build function
    const _apply = () => {
      this.dataGrid.applyConfig({
        primaryKeyField: this.primaryKeyField,
        columns: this.columns,
        server: this.server ? this.server : undefined,
        isRowEditableCondition: this.isRowEditableCondition,
        saveOnExit: this.saveOnExit,
        actionColumnRenderer: this.actionColumnRenderer,
        enableRowEdit: this.enableRowEdit,
        isRowDisabledCondition: this.isRowDisabledCondition,
        agGridOptionValues: this.agGridOptionValues,
      });
    };

    // Configure AG Grid directly here
    const _agGridOptionValues = (
      agGridOptionValues: GridOptions
    ): { apply: () => void } => {
      this.agGridOptionValues = agGridOptionValues;
      return {
        apply: _apply,
      };
    };

    // Add methods (like enableRowEditing) here to configure KatalDataGrid
    const builder: SubBuilder = {
      enableRowEditing: (
        { isRowEditableCondition, saveOnExit = false } = { saveOnExit: false }
      ) => {
        if (isRowEditableCondition) {
          this.isRowEditableCondition = isRowEditableCondition;
        }
        this.enableRowEdit = true;
        this.saveOnExit = saveOnExit;
        delete builder.enableRowEditing;

        return builder;
      },
      disabledRows: isRowDisabledCondition => {
        this.isRowDisabledCondition = isRowDisabledCondition;
        delete builder.disabledRows;
        return builder;
      },
      actionColumnRenderer: actionColumnRenderer => {
        this.actionColumnRenderer = actionColumnRenderer;
        delete builder.actionColumnRenderer;
        return builder;
      },
      agGridOptionValues: _agGridOptionValues,
      apply: _apply,
    };

    return builder;
  }
}
